import {
  Box,
  Button,
  Container,
  Header,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-console";
import React, { useCallback, useMemo, useRef, useState } from "react";

import { PreviewGroup } from "@/contexts/ClipPreviewContext";
import {
  canonicalFilters,
  loadAssetById,
  loadPlayAssets,
} from "@/hooks/cart/useAssets";
import useGroupedPlayAssets from "@/hooks/cart/useGroupedPlayAssets";
import { useApi } from "@/hooks/useApi";

import {
  AnyIconikItem,
  AssetIdIconikItem,
  InitialIconikItem,
  MetadataFilters,
} from "../chat/types";
import IconikAssetsTable from "./IconikAssetsTable";
import NoMediaCard from "./NoMediaCard";

export interface IconikExpandablesProps {
  items: AnyIconikItem[];
  /**
   * Response-level metadata filters parsed from the agent answer, threaded to
   * each play's ``/assets`` fetch so the delivered clips are narrowed. Absent
   * means the answer had no metadata filters (unchanged behaviour).
   */
  filters?: MetadataFilters;
}

const isAssetIdItem = (item: AnyIconikItem): item is AssetIdIconikItem =>
  "assetId" in item;

const isGameIdItem = (item: AnyIconikItem): item is InitialIconikItem =>
  "gameId" in item;

/**
 * Renders the clip accordions for a chat answer.
 *
 * Plays are coordinated through {@link useGroupedPlayAssets} so the layout is
 * deterministic: accordions that have media are sorted to the top (one
 * expandable per play, fetched from the shared cache), and every play with no
 * media is collapsed into a single "No Media Found" card below them.
 *
 * For multi-sport (AssetIdIconikItem), assets are fetched directly by ID
 * without the grouped-play coordination since there's no game/play structure.
 */
/** Stable section key for an asset-id (multi-sport) accordion. */
const assetSectionKey = (item: AssetIdIconikItem): string =>
  `asset-${item.assetId}`;

/** Stable section key for a game/play (NBA) accordion. */
const playSectionKey = (item: InitialIconikItem): string =>
  `${item.gameId}-${item.playId}`;

const IconikExpandables: React.FC<IconikExpandablesProps> = ({
  items,
  filters,
}) => {
  // Separate asset-id items from game-id items. Memoized on the items array so
  // the derived lists keep a stable identity across this component's own
  // re-renders — without it the `expandableKeys` memo below is recomputed every
  // render, and `gameIdItems` changing identity re-runs the grouping hook's
  // effect key derivation for no reason.
  const assetIdItems = useMemo(() => items.filter(isAssetIdItem), [items]);
  const gameIdItems = useMemo(() => items.filter(isGameIdItem), [items]);
  const { apiGet } = useApi();

  // Use the grouped play hook only for game-id items (NBA path)
  const { withMedia, noMedia, pending } = useGroupedPlayAssets(
    gameIdItems,
    filters
  );

  // Per-section expanded state, tracked as the *collapsed* keys. Sections
  // default to open, so a key is present here only while its accordion has
  // been closed. Storing the exception rather than the rule is what makes the
  // default survive incremental reveal: plays resolve one at a time, and an
  // accordion that appears after the initial render has no entry here, so it
  // opens without the parent having to notice it and seed state for it.
  //
  // Owning this centrally lets the toolbar collapse/expand every visible
  // section at once while still allowing individual sections to be toggled.
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(
    () => new Set()
  );

  // Keys of the sections that actually render as expandable accordions:
  // every asset-id item plus the with-media plays. No-media plays collapse
  // into a single non-expandable card, so they are excluded from expand-all.
  const expandableKeys = useMemo(
    () => [
      ...assetIdItems.map(assetSectionKey),
      ...withMedia.map(playSectionKey),
    ],
    [assetIdItems, withMedia]
  );

  // Canonical form of the filters, so the sequence below memoizes on their
  // *contents*. The filters object is re-parsed from the answer's markdown on
  // every render, so keying the memo on its identity would rebuild the whole
  // sequence and all of its thunks on every render of the message.
  const filtersKey = canonicalFilters(filters);

  // The answer's groups in the order they appear on screen: multi-sport cards
  // first, then the with-media plays, matching the render order below. Handed
  // to the Clip Preview so its arrows can step from the last clip of one group
  // into the first clip of the next.
  //
  // Clips are not resolved here. Each entry carries a thunk over the same
  // cache-backed loaders the accordions use, so a group already on screen costs
  // nothing until the user actually arrows into it. The no-media plays are
  // excluded: they have no clips, so they are not stops on the way through.
  const previewSequence = useMemo<PreviewGroup[]>(
    () => [
      ...assetIdItems.map((item) => ({
        key: assetSectionKey(item),
        // The multi-sport path has no game/play, matching how `enumerate` tags
        // these assets.
        gameId: "",
        playId: "",
        load: () =>
          loadAssetById(apiGet, item.assetId).then((result) => result.items),
      })),
      ...withMedia.map((item) => ({
        key: playSectionKey(item),
        gameId: item.gameId,
        playId: item.playId,
        load: () =>
          loadPlayAssets(apiGet, item.gameId, item.playId, filters).then(
            (result) => result.items
          ),
      })),
    ],
    // filtersKey stands in for `filters`, which is read inside the thunks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetIdItems, withMedia, filtersKey, apiGet]
  );

  // Handed to the accordions as a getter rather than as the array itself.
  //
  // The sequence necessarily changes identity every time a play resolves — a
  // new group joins it — and plays resolve one at a time. Passing the array
  // down would therefore give every card already on screen a new prop on each
  // of those renders, defeating `memo(IconikAssetsTable)` in exactly the way
  // the stable `expandHandlerFor` handlers below exist to avoid. A getter whose
  // identity never changes keeps the children memoized.
  //
  // The ref is assigned during render, which is safe here because it is never
  // read during render: only inside the accordions' click handlers and effects,
  // both of which run after this assignment has taken place for the current
  // render.
  const previewSequenceRef = useRef<PreviewGroup[]>([]);
  previewSequenceRef.current = previewSequence;
  const getPreviewSequence = useCallback(() => previewSequenceRef.current, []);

  const setSectionExpanded = useCallback((key: string, expanded: boolean) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (expanded) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // One stable callback per section key.
  //
  // This matters now that accordions are revealed incrementally: this component
  // re-renders once per play that resolves, and an inline
  // `onExpandChange={(e) => setSectionExpanded(key, e)}` would hand every
  // already-visible card a new function identity on each of those renders,
  // defeating `memo(IconikAssetsTable)` and re-rendering the whole answer's
  // clip tables N times for N plays. Cached in a ref rather than derived, so
  // identities survive re-renders; keys are stable domain identifiers
  // (asset id, or game/play), so the map cannot grow unboundedly for a message.
  const expandHandlers = useRef(new Map<string, (expanded: boolean) => void>());
  const expandHandlerFor = useCallback(
    (key: string) => {
      const cached = expandHandlers.current.get(key);
      if (cached) return cached;
      const handler = (expanded: boolean) => setSectionExpanded(key, expanded);
      expandHandlers.current.set(key, handler);
      return handler;
    },
    [setSectionExpanded]
  );

  const allExpanded =
    expandableKeys.length > 0 &&
    expandableKeys.every((key) => !collapsedKeys.has(key));

  const toggleAll = useCallback(() => {
    setCollapsedKeys(allExpanded ? new Set(expandableKeys) : new Set());
  }, [allExpanded, expandableKeys]);

  const hasExpandableSections = expandableKeys.length > 0;

  return (
    <Box padding={{ vertical: "s" }}>
      <SpaceBetween size="m">
        {/* Toolbar: one control to expand or collapse every clip accordion.
            Only shown once there is at least one expandable section. */}
        {hasExpandableSections && (
          <Box textAlign="right">
            <Button
              variant="inline-link"
              iconName={allExpanded ? "angle-up" : "angle-down"}
              iconAlign="right"
              onClick={toggleAll}
              ariaLabel={
                allExpanded
                  ? "Collapse all clip results"
                  : "Expand all clip results"
              }
            >
              {allExpanded ? "Collapse all" : "Expand all"}
            </Button>
          </Box>
        )}

        {/* Multi-sport: render directly by asset ID */}
        {assetIdItems.map((item) => {
          const key = assetSectionKey(item);
          return (
            <IconikAssetsTable
              key={key}
              assetId={item.assetId}
              expanded={!collapsedKeys.has(key)}
              onExpandChange={expandHandlerFor(key)}
              getPreviewSequence={getPreviewSequence}
              previewGroupKey={key}
            />
          );
        })}

        {/* NBA path: grouped play rendering.
            Accordions appear as each play's fetch lands instead of waiting for
            the whole group, so one slow play no longer hides the answers that
            are already available. The no-media card is held back until every
            play has settled — it aggregates them into a single card, which
            would otherwise grow and reflow as results arrive. */}
        {gameIdItems.length > 0 && (
          <>
            {withMedia.map((item) => {
              const key = playSectionKey(item);
              return (
                <IconikAssetsTable
                  key={key}
                  gameId={item.gameId}
                  playId={item.playId}
                  filters={filters}
                  expanded={!collapsedKeys.has(key)}
                  onExpandChange={expandHandlerFor(key)}
                  getPreviewSequence={getPreviewSequence}
                  previewGroupKey={key}
                />
              );
            })}
            {noMedia.length > 0 && <NoMediaCard entries={noMedia} />}
            {pending > 0 && (
              <Container>
                <Header info={<Spinner />}>
                  {`Loading ${pending} of ${gameIdItems.length} ${
                    gameIdItems.length === 1 ? "clip" : "clips"
                  }...`}
                </Header>
              </Container>
            )}
          </>
        )}
      </SpaceBetween>
    </Box>
  );
};

export default IconikExpandables;
